// The `localStorage.profile` invariant.
//
// A dozen components read this key and assume it exists — the sidebar calls
// logout() when it can't, and easyPay / the excel modal / fund tabs / the
// support modal all do `JSON.parse(localStorage.getItem('profile')!)` and throw
// on null. That assumption is fine, but only if something guarantees it.
//
// Historically nothing did: the key is written by exactly one page,
// /auth/profile, and the SMS login happens to route through it. Every login
// path added since (mini-app auto-login, admin impersonate) skipped that page
// and left the app authenticated but unusable.
//
// So rather than teaching twelve consumers to tolerate a missing profile, this
// runs once at startup and makes the invariant true for *every* entry point,
// present and future: if there is a session, there is a profile.

import { identifyPerson } from 'utilities/analytics'
import { getAuthProfile } from 'services/auth'

export const hasSession = (): boolean => {
    try {
        const stored = window.localStorage.getItem('user')
        return !!stored && stored !== 'null' && !!JSON.parse(stored)?.token
    } catch {
        return false
    }
}

/**
 * Marks a session that support opened on a member's behalf (`/auth/impersonate`).
 *
 * Lives in localStorage beside the token, so it ends when the session does:
 * logout and the 401 handler both clear the whole store. Anything that would
 * tie THIS device or THIS person to the member — binding a messenger account,
 * say — has to check it first.
 */
const SUPPORT_SESSION_KEY = 'session:support'

export const markSupportSession = (): void => {
    try {
        window.localStorage.setItem(SUPPORT_SESSION_KEY, '1')
    } catch {
        // Storage refused: the caller is about to fail at login anyway.
    }
}

export const isSupportSession = (): boolean => {
    try {
        return window.localStorage.getItem(SUPPORT_SESSION_KEY) === '1'
    } catch {
        return false
    }
}

export const hasProfile = (): boolean => {
    try {
        const stored = window.localStorage.getItem('profile')
        return !!stored && stored !== 'null' && !!JSON.parse(stored)
    } catch {
        return false
    }
}

interface CachedProfile {
    id?: number
    first_name?: string
    last_name?: string
    email?: string
    phone_number?: string
}

const readCachedProfile = (): CachedProfile | null => {
    try {
        const stored = window.localStorage.getItem('profile')
        return stored ? JSON.parse(stored) : null
    } catch {
        return null
    }
}

/**
 * Attach this session to the person who owns it.
 *
 * Buffered, not sent: this runs before the first render and therefore before
 * PostHog is initialised, where a direct `posthog.identify()` is a silent
 * no-op. Cheap to repeat — posthog only emits `$identify` when it is actually
 * moving an anonymous visitor onto a person.
 */
const identifyFromProfile = (profile: CachedProfile | null) => {
    identifyPerson(profile?.phone_number, {
        user_id: profile?.id,
        name: profile?.first_name + ' ' + profile?.last_name,
        email: profile?.email,
        phone_number: profile?.phone_number,
    })
}

/**
 * Fetch and cache the profile, mirroring what the /auth/profile page does —
 * including its identify() call, so a session started anywhere maps to the
 * same PostHog person as one started on the web.
 *
 * Returns whether the profile carries a name; a member created from a shared
 * contact has none yet and still needs the form.
 */
export const fetchAndCacheProfile = async (): Promise<boolean> => {
    const profile = await getAuthProfile()
    window.localStorage.setItem('profile', JSON.stringify(profile))
    identifyFromProfile(profile)
    return !!profile?.first_name?.length
}

/**
 * Guarantee the invariant before the first render.
 *
 * Awaited rather than backgrounded on purpose: consumers read the key
 * synchronously while rendering, so a background fetch would race them and
 * turn a deterministic bug into an intermittent one.
 *
 * Costs nothing in the common case — a web user who logged in normally already
 * has the key, and a logged-out visitor has no session to complete.
 *
 * Returns the path we should start on, or null to leave routing alone.
 */
export const ensureSessionProfile = async (): Promise<string | null> => {
    if (!hasSession()) return null

    // Identify from what we already have, and only then consider returning.
    //
    // This used to be a side effect of the fetch below, which meant identifying
    // happened exactly once — on the single launch where the profile cache was
    // cold — and never again for the life of the install. Anyone who already
    // had `profile` in localStorage when the identify code shipped stayed
    // anonymous forever. In the mini apps, where the cache is warm on every
    // launch after the first, that was 166 of 169 launches landing on a UUID
    // person with no phone number, no history and no funnel.
    //
    // Identity is not a cache-miss concern. If there is a session, say who it
    // belongs to.
    if (hasProfile()) {
        identifyFromProfile(readCachedProfile())
        return null
    }

    let named: boolean
    try {
        named = await fetchAndCacheProfile()
    } catch {
        // Token rejected, or the network is down. Leave it alone: the first
        // API call will 401 and the interceptor routes to the SMS login.
        return null
    }

    // No name yet (typically a member created from a shared contact). Send them
    // through the same form the SMS flow would have shown — the normal path,
    // not a special case.
    return named ? null : '/auth/profile'
}
